import { axios } from "./crud";
import { getQueryParams } from "../lib/helpers/strings";

const auth = {
  login: async ({ username, password }) =>
    await axios.login("/api/v1/auth/login", { username, password })
};

const me = {
  getInfo: async () => await axios.get("/api/v1/auth/me"),
  update: async params => await axios.put("/api/v1/auth/me", params),
  changePassword: async ({ password, passwordNew }) =>
    await axios.patch("/api/v1/auth/password", { password, passwordNew })
};

const settings = {
  template: {
    getList: async () => await axios.get(`/api/v1/template`),
    createOne: async data => await axios.post(`/api/v1/template`, data),
    getOne: async id => await axios.get(`/api/v1/template/${id}`),
    testOne: async (id, filialId, query, requestParams) =>
      await axios.get(
        `/api/v1/template/${id}/test/${filialId}${getQueryParams(query)}`,
        requestParams
      ),
    editOne: async (id, data) =>
      await axios.post(`/api/v1/template/${id}`, data),
    deleteOne: async id => await axios._delete(`/api/v1/template/${id}`)
  },
  marker: {
    getList: async () => await axios.get("/api/v1/markers"),
    createOne: async data => await axios.post("/api/v1/markers", data),
    editOne: async (id, data) =>
      await axios.post(`/api/v1/markers/${id}`, data),
    deleteOne: async id => await axios._delete(`/api/v1/markers/${id}`)
  }
};

const global = {
  search: async search =>
    await axios.get("/api/v1/search?text=" + (search || ""))
};

const bilets = {
  getList: async (query, requestParams) =>
    await axios.get("/api/v1/bilets" + getQueryParams(query), requestParams),
  getOne: async biletId => await axios.get(`/api/v1/bilets/${biletId}`),
  getBiletHistorys: async biletId =>
    await axios.get(`/api/v1/history?source=bilets&id=${biletId}`),
  getOneHistory: async historyId =>
    await axios.get(`/api/v1/history/${historyId}`),
  getBiletBlanks: async (biletId, query) =>
    await axios.get(
      `/api/v1/blanks?bilet=${biletId}` + getQueryParams(query, true)
    ),
  getBiletItems: async (biletId, query) =>
    await axios.get(
      `/api/v1/items?bilet=${biletId}` + getQueryParams(query, true)
    ),
  editOne: async (biletId, params) =>
    await axios.put(`/api/v1/bilets/${biletId}`, { ...params }),
  editOneHistory: async (historyId, params) =>
    await axios.put(`/api/v1/history/bilets/${historyId}`, { ...params }),
  createFine: async (biletId, params) =>
    await axios.post(`/api/v1/bilets/${biletId}/fine`, { ...params }),
  editFine: async (biletId, fineId, params) =>
    await axios.put(`/api/v1/bilets/${biletId}/fine/${fineId}`, { ...params }),
  editFineHistory: async (historyId, fineId, params) =>
    await axios.put(`/api/v1/history/bilets/${historyId}/fine/${fineId}`, {
      ...params
    }),
  deleteOne: async biletId => await axios._delete(`/api/v1/bilets/${biletId}`),
  deleteFine: async (biletId, fineId) =>
    await axios._delete(`/api/v1/bilets/${biletId}/fine/${fineId}`),
  deleteFineHistory: async (historyId, fineId) =>
    await axios._delete(`/api/v1/history/bilets/${historyId}/fine/${fineId}`)
};

const blanks = {
  getList: async (query, requestParams) =>
    await axios.get("/api/v1/blanks" + getQueryParams(query), requestParams),
  getOne: async blankId => await axios.get(`/api/v1/blanks/${blankId}`),
  getBlankHistorys: async blankId =>
    await axios.get(`/api/v1/history?source=blanks&id=${blankId}`),
  getOneHistory: async historyId =>
    await axios.get(`/api/v1/history/${historyId}`),
  editOne: async (blankId, params) =>
    await axios.put(`/api/v1/blanks/${blankId}`, { ...params }),
  editOneHistory: async (historyId, params) =>
    await axios.put(`/api/v1/history/blanks/${historyId}`, { ...params })
};

const filials = {
  getList: async (query, requestParams) =>
    await axios.get("/api/v1/filials" + getQueryParams(query), requestParams),
  getOne: async filialId => await axios.get(`/api/v1/filials/${filialId}`),
  createOne: async data => await axios.post(`/api/v1/filials/`, data),
  getMoneyOperations: async (filialId, page, params) =>
    await axios.get(
      `/api/v1/history?id=${filialId}` +
        getQueryParams(
          {
            source: "filials",
            method: "money",
            limit: 50,
            page: page,
            countDocuments: false,
            select:
              "_id " +
              "_options.type _options.sum _options.cash_desk _options.cash_desk_new _options.bilet._id _options.bilet.bilet_number " +
              "_method user_id _type _document created_at updated_at " +
              "_field._id _field.cash_desk"
          },
          true
        ),
      { ...params }
    ),
  getOneMoneyOperation: async historyId =>
    await axios.get(
      `/api/v1/history/${historyId}` +
        getQueryParams({
          select:
            "_id " +
            "_options.type _options.sum _options.cash_desk _options.cash_desk_new _options.bilet._id _options.bilet.bilet_number " +
            "_method user_id _type _document created_at updated_at " +
            "_field._id _field.cash_desk"
        })
    ),
  editOne: async (filialId, params) =>
    await axios.put(`/api/v1/filials/${filialId}`, { ...params }),
  editOnePrices: async (filialId, pricesId, params) =>
    await axios.put(`/api/v1/filials/${filialId}/prices/${pricesId}`, {
      ...params
    }),
  editOneMoneyOperation: async (historyId, params) =>
    await axios.put(`/api/v1/history/money/${historyId}`, { ...params })
};

const roles = {
  getList: async (query, requestParams) =>
    await axios.get("/api/v1/roles" + getQueryParams(query), requestParams)
};

const users = {
  getList: async (query, requestParams) =>
    await axios.get("/api/v1/users" + getQueryParams(query), requestParams),
  getOne: async userId => await axios.get(`/api/v1/users/${userId}`),
  createOne: async (data, requestParams = {}) =>
    await axios.post("/api/v1/users", data, requestParams),
  editOne: async (userId, params) =>
    await axios.put(`/api/v1/users/${userId}`, { ...params }),
  changePassword: async (userId, { passwordNew }) =>
    await axios.patch(`/api/v1/users/${userId}`, { passwordNew })
};

const depositors = {
  getList: async (query, requestParams) =>
    await axios.get(
      "/api/v1/depositors" + getQueryParams(query),
      requestParams
    ),
  getOne: async depositorId =>
    await axios.get(`/api/v1/depositors/${depositorId}`),
  editOne: async (depositorId, params) =>
    await axios.put(`/api/v1/depositors/${depositorId}`, { ...params })
};

const reports = {
  getOneMonth: async (query, params) =>
    await axios.get(`/api/v1/reports/month` + getQueryParams(query), {
      ...params
    }),
  getOne: async (query, params) =>
    await axios.get(
      `/api/v1/reports/day` +
        getQueryParams({
          withHistory: true,
          populateHistory: true,
          ...query
        }),
      { ...params, noMessage: true }
    ),
  getOneById: async (reportId, params) =>
    await axios.get(`/api/v1/reports/day/${reportId}`, { ...params }),
  editOne: async (reportId, params) =>
    await axios.put(`/api/v1/reports/day/${reportId}`, { ...params }),
  deleteOneById: async (reportId, params) =>
    await axios._delete(`/api/v1/reports/day/${reportId}`, { ...params }),
  // Date format = DD.MM.YYYY
  createForDate: async (date, params) =>
    await axios.post(`/api/v1/reports/day/${date}`, { ...params })
};

const widgets = {
  getLoginList: async params =>
    await axios.get(`/api/v1/widgets/users/login-list`, {
      ...params
    }),
  getBiletsCountByTypes: async params =>
    await axios.get(`/api/v1/widgets/bilets/count-by-types`, {
      ...params
    }),
  getMaterialBase: async params =>
    await axios.get(`/api/v1/widgets/bilets/material-base`, {
      ...params
    })
};

const helpers = {
  widgets,
  reports,
  depositors,
  users,
  roles,
  filials,
  blanks,
  bilets,
  global,
  settings,
  me,
  auth
};

export default helpers;
